import axios from "axios";
import { tokenManager } from "@/utils/tokenManager";

const RAG_API_URL =
  window.env?.REACT_APP_RAG_API_URL || "http://localhost:8002/api/v1";

const ragApi = axios.create({
  baseURL: RAG_API_URL,
});

tokenManager.setupInterceptors(ragApi);

export interface ApiKeyCreate {
  name: string;
  project_id: string;
  scopes?: string[];
  expires_at?: string | null;
  max_budget_usd?: number | null;
  budget_reset_frequency?: string | null;
}

export interface ApiKey {
  id: string;
  user_id: string;
  name: string;
  project_id?: string | null;
  project_name?: string | null;
  scopes: string[];
  expires_at: string | null;
  is_active: boolean;
  last_used_at: string | null;
  created_at: string;
  key?: string | null;
  secret_key?: string | null;
  is_synced_with_litellm?: boolean;
  litellm_token_hash?: string | null;
  litellm_expires_at?: string | null;
  litellm_spend?: string | null;
  litellm_created_at?: string | null;
  litellm_created_by?: string | null;
  litellm_updated_at?: string | null;
  litellm_updated_by?: string | null;
}

export interface ApiKeyWithValue extends ApiKey {
  secret_key?: string | null;
}

export interface RotateApiKeyResponse {
  old_key_id: string;
  new_key: ApiKeyWithValue;
}

export interface ApiKeysMetadata {
  page: number;
  page_size: number;
  total_items: number;
  total_pages: number;
}

export interface PaginatedApiKeysResponse {
  items: ApiKey[];
  metadata: ApiKeysMetadata;
}

export const apiKeysRagApi = {
  listApiKeys: async (
    page: number = 1,
    pageSize: number = 10,
    projectIds?: string[],
  ): Promise<PaginatedApiKeysResponse> => {
    const params = new URLSearchParams();
    params.set("page", String(page));
    params.set("page_size", String(pageSize));
    projectIds
      ?.filter((id) => Boolean(id) && id !== "all")
      .forEach((id) => params.append("project_ids", id));
    const response = await ragApi.get("/api-keys/", { params });
    return response.data;
  },

  getApiKey: async (id: string): Promise<ApiKey> => {
    const response = await ragApi.get(`/api-keys/${id}`);
    return response.data;
  },

  createApiKey: async (data: ApiKeyCreate): Promise<ApiKeyWithValue> => {
    const response = await ragApi.post("/api-keys/", {
      ...data,
      scopes: data.scopes?.length ? data.scopes : ["llm"],
    });
    return response.data;
  },

  deleteApiKey: async (id: string): Promise<void> => {
    await ragApi.delete(`/api-keys/${id}`);
  },

  rotateApiKey: async (id: string): Promise<RotateApiKeyResponse> => {
    const response = await ragApi.post(`/api-keys/${id}/rotate`);
    return response.data;
  },
};
